import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Anchor,
  Box,
  Breadcrumbs,
  Button,
  Card,
  //Group,
  Stack,
  Table,
  Title,
} from "@mantine/core";
import { v4 as uuidv4 } from "uuid";

import {
  ADMIN_ORGANIZATION,
  BINGO_CREATE_AUTHORISED_ORGANIZATIONS,
  BINGO_CREATE_AUTHORISED_ROLES,
  BINGO_ORGANIZATION_VISIBILITY_AUTHORISED_ORGANIZATIONS,
  BINGO_ORGANIZATION_VISIBILITY_AUTHORISED_ROLES,
} from "_roles";

import type { BingoGame } from "bingo/types/bingo-game.model";

import { useOrganization } from "common/admin/auth/hooks";
//import { useDebug } from "common/debug/hooks/useDebug";
import {
  useListBingoGamesQuery,
  useRemoveBingoGameMutation,
} from "bingo/api/bingo-game.api";

import { AccessRights } from "common/admin/auth/AccessRights";
import QueryWrapper from "common/components/QueryWrapper";
import { Th } from "common/components/table/Th";
import { BingoGameAdminRow } from "bingo/components/BingoGameAdminRow";
import { ADMIN_BINGO_PATH, ADMIN_PATH } from "_urls";

type SortedKeys = keyof Pick<BingoGame, "name" | "creationTime" | "access">;

function sortData(
  data: BingoGame[],
  { sortBy, reversed }: { sortBy: SortedKeys; reversed: boolean },
): BingoGame[] {
  return [...data].sort((a, b) =>
    reversed
      ? b[sortBy].toString().localeCompare(a[sortBy].toString())
      : a[sortBy].toString().localeCompare(b[sortBy].toString()),
  );
}

export function BingoGamesAdminListView() {
  //const { isDebugMode, log } = useDebug();
  const navigate = useNavigate();
  const [organization] = useOrganization();
  const listBingoGamesQuery = useListBingoGamesQuery();
  const [removeBingoGame] = useRemoveBingoGameMutation();

  const [sortedData, setSortedData] = useState<BingoGame[]>([]);
  const [sortBy, setSortBy] = useState<SortedKeys>("creationTime");
  const [reverseSortDirection, setReverseSortDirection] = useState(false);

  const bingoGames = useMemo(
    () => listBingoGamesQuery.data ?? [],
    [listBingoGamesQuery.data],
  );

  const filteredBingoGames = useMemo(
    () =>
      (bingoGames || []).filter(
        (bingoGame) => bingoGame.creationTime && bingoGame.name,
      ),
    [bingoGames],
  );

  const triggerSortingData = useCallback(
    (field: SortedKeys) => {
      const reversed = field === sortBy ? !reverseSortDirection : false;
      return sortData(filteredBingoGames, { sortBy: field, reversed });
    },
    [filteredBingoGames, sortBy, reverseSortDirection],
  );

  useEffect(() => {
    setSortedData(triggerSortingData(sortBy));
  }, [filteredBingoGames, sortBy, triggerSortingData]);

  const remove = async (id: string) => {
    try {
      await removeBingoGame(id);
      console.log(`Bingo Game ${id} removed successfully`);
    } catch (error) {
      console.log(`Remove error: ${error}`);
    }
  };

  return (
    <Box>
      <div
        className="sticky top-0 z-50 pb-4"
        style={{ backgroundColor: "#E9ECEF" }}
      >
        <Title order={3} align={"center"} m={"xs"}>
          Bingo games
        </Title>
        <Breadcrumbs separator="→">
          <Anchor component={Link} to={`/${ADMIN_PATH}`}>
            Admin
          </Anchor>

          <Anchor component={Link} to={`/${ADMIN_PATH}/${ADMIN_BINGO_PATH}`}>
            Bingo
          </Anchor>

          <span>Bingo Games</span>
        </Breadcrumbs>

        <AccessRights
          authorizedOrganizations={BINGO_CREATE_AUTHORISED_ORGANIZATIONS}
          authorizedRoles={BINGO_CREATE_AUTHORISED_ROLES}
        >
          {/* <Group position={"right"}>
          <Button className="bg-button-admin" component={Link} to="new">
            Ajouter un Bingo game
          </Button>
        </Group>*/}
        </AccessRights>
      </div>
      <Card shadow={"sm"} p={"xl"} className={"overflow-visible"}>
        <QueryWrapper query={listBingoGamesQuery}>
          {bingoGames.length > 0 ? (
            <Table highlightOnHover>
              <thead>
                <tr>
                  <Th
                    sorted={sortBy === "name"}
                    reversed={reverseSortDirection}
                    onClick={() => {
                      setSortBy("name");
                      setReverseSortDirection(
                        sortBy === "name" ? !reverseSortDirection : false,
                      );
                    }}
                  >
                    Name
                  </Th>
                  <AccessRights
                    authorizedOrganizations={
                      BINGO_ORGANIZATION_VISIBILITY_AUTHORISED_ORGANIZATIONS
                    }
                    authorizedRoles={
                      BINGO_ORGANIZATION_VISIBILITY_AUTHORISED_ROLES
                    }
                  >
                    <Th
                      sorted={sortBy === "access"}
                      reversed={reverseSortDirection}
                      onClick={() => {
                        setSortBy("access");
                        setReverseSortDirection(
                          sortBy === "access" ? !reverseSortDirection : false,
                        );
                      }}
                    >
                      Organization
                    </Th>
                  </AccessRights>
                  <Th
                    sorted={sortBy === "creationTime"}
                    reversed={reverseSortDirection}
                    onClick={() => {
                      setSortBy("creationTime");
                      setReverseSortDirection(
                        sortBy === "creationTime"
                          ? !reverseSortDirection
                          : false,
                      );
                    }}
                  >
                    Creation Date
                  </Th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((bingoGm) =>
                  bingoGm.access.organization === organization ||
                  organization === ADMIN_ORGANIZATION ? (
                    <BingoGameAdminRow
                      key={uuidv4()}
                      bingoGame={bingoGm}
                      onEdit={() => navigate(bingoGm.id)}
                      onDelete={() => remove(bingoGm.id)}
                    />
                  ) : null,
                )}
              </tbody>
            </Table>
          ) : (
            <Stack align={"center"}>
              <Box>Aucun Bingo game trouvé</Box>
              <Button className="bg-button-admin" component={Link} to="new">
                Ajouter un Bingo game
              </Button>
            </Stack>
          )}
        </QueryWrapper>
      </Card>
    </Box>
  );
}
